import { formatDanishCivilDate } from '@/lib/danishDate';
import { clinicDayKey } from '@/utils/calendarClinicTime';

/** Dokumentnavn when it has text; otherwise the file's display filename. */
export function patientFileListName(row: {
  original_filename: string;
  document_title?: string | null;
}): string {
  const title = row.document_title?.trim() ?? '';
  return title.length > 0 ? title : row.original_filename;
}

/** Keep a stored document date. Blank means the file has no document date. */
export function storedDocumentDate(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/** Danish civil date for a stored document_date, or null when the value is empty. */
export function formatPatientFileDocumentDate(value: string | null | undefined): string | null {
  const stored = storedDocumentDate(value);
  if (!stored) return null;
  const formatted = formatDanishCivilDate(stored.slice(0, 10));
  return formatted || null;
}

/**
 * Calendar days that mean "the file was added", not a letter/scan date.
 * The column default is CURRENT_DATE (UTC day of the insert) and the backfill
 * copied the Europe/Copenhagen day of created_at.
 */
export function uploadFallbackDays(createdAt: string | null | undefined): string[] {
  if (!createdAt) return [];
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return [];
  const days = new Set<string>();
  const utcDay = createdAt.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(utcDay)) days.add(utcDay);
  days.add(clinicDayKey(parsed));
  return [...days];
}

/**
 * Document date to show in Filer. Blank when the field is empty or only holds
 * the system/upload day. A date that differs from that day was set on purpose.
 */
export function explicitDocumentDateIso(row: {
  document_date?: string | null;
  created_at?: string | null;
}): string | null {
  const stored = storedDocumentDate(row.document_date);
  if (!stored) return null;
  const day = stored.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  if (uploadFallbackDays(row.created_at).includes(day)) return null;
  return day;
}

export function explicitPatientFileDocumentDate(row: {
  document_date?: string | null;
  created_at?: string | null;
}): string | null {
  const iso = explicitDocumentDateIso(row);
  return iso ? formatDanishCivilDate(iso) : null;
}
